import { useState, useEffect, useCallback } from "react";
import { T } from "../../theme";
import { apiCall, apiCallEx } from "../../apiClient";
import { Btn, Inp, Modal } from "../primitives";

// Client Entities (firm edition, 2026-07-02 display-code lane). NET-NEW
// surface: clients were previously created only inline (AI Import /
// Repository) and had no admin panel at all — display codes were set once by
// migration and unwritable, so post-migration clients rendered UNK forever.
// This panel manages the per-client display code: auto-suggest from the name,
// registry-validated (409 for any code ever used — never reused), 4 letters
// for new codes; legacy 3-char codes render as stamped and stay reserved.
// Changing a code affects FUTURE records only.
export const ClientEntitiesManager = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [editClient, setEditClient] = useState<any>(null);
  const [code, setCode] = useState("");
  const [saveError, setSaveError] = useState("");

  const load = useCallback(async () => {
    const r = await apiCall("/clients");
    const arr = Array.isArray(r) ? r : (r?.clients || []);
    setClients(arr);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openEdit = async (c: any) => {
    setEditClient(c);
    setSaveError("");
    if (c.display_code) { setCode(c.display_code); return; }
    setCode("");
    const r = await apiCall(`/clients/suggest-code?name=${encodeURIComponent(c.name || "")}`);
    if (r?.code) setCode(r.code);
  };

  const handleSave = async () => {
    if (!editClient) return;
    setSaveError("");
    const r = await apiCallEx(`/clients/${editClient.id}/display-code`, { method: "PUT", body: JSON.stringify({ display_code: code }) });
    if (!r.ok) { setSaveError(r.data?.error || "Save failed"); return; }
    setEditClient(null);
    void load();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Client Entities</div>
        <div style={{ fontSize: 11, color: T.txD }}>Clients are created from AI Import / Repository; codes are managed here.</div>
      </div>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead><tr style={{ borderBottom: "2px solid " + T.brd }}>
          <th style={{ textAlign: "left", padding: 8 }}>Name</th>
          <th style={{ textAlign: "center", padding: 8 }}>Display Code</th>
          <th style={{ textAlign: "center", padding: 8 }}>Active</th>
          <th style={{ textAlign: "right", padding: 8 }}>Actions</th>
        </tr></thead>
        <tbody>
          {clients.map(c => (
            <tr key={c.id} style={{ borderBottom: "1px solid " + T.brd }}>
              <td style={{ padding: 8 }}>{c.name}</td>
              <td style={{ padding: 8, textAlign: "center", fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>{c.display_code || "—"}</td>
              <td style={{ padding: 8, textAlign: "center" }}>{c.is_active !== false ? "✓" : "✗"}</td>
              <td style={{ padding: 8, textAlign: "right" }}>
                <span onClick={() => { void openEdit(c); }} style={{ color: T.acc, cursor: "pointer", fontSize: 11 }}>{c.display_code ? "Change Code" : "Set Code"}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editClient && (
        <Modal open onClose={() => setEditClient(null)} title={`Display code — ${editClient.name}`}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Inp label="Display Code (4 letters)" value={code} onChange={(v: string) => setCode(v.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))} placeholder="auto-suggested from name" />
            <div style={{ fontSize: 10, color: T.txD }}>Stamped on new records (REQ-{code || "CODE"}-0001, per-client numbering). Codes are never reused; changing it affects future records only — existing records keep the code they were created under.</div>
            {saveError && <div style={{ fontSize: 12, color: T.red }}>{saveError}</div>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <Btn v="secondary" onClick={() => setEditClient(null)}>Cancel</Btn>
              <Btn v="primary" onClick={() => { void handleSave(); }}>Save</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
